import React, { useState } from "react";

import { Download, FileSpreadsheet, Info, Loader2, Upload } from "lucide-react";
import * as XLSX from "xlsx";

import { Alert, AlertDescription } from "~/components/ui/alert";
import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Input } from "~/components/ui/input";
import { Textarea } from "~/components/ui/textarea";
import { useToast } from "~/hooks/use-toast";
import { supabase } from "~/integrations/supabase/client";
import { Product } from "~/types/product";

interface ProductImportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onImport: (
    products: Partial<Product>[],
    updateMode?: boolean
  ) => Promise<void>;
  products: Product[]; // Current products for template generation
}

export const ProductImportDialog: React.FC<ProductImportDialogProps> = ({
  open,
  onOpenChange,
  onImport,
  products,
}): JSX.Element => {
  const [csvData, setCsvData] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [importMethod, setImportMethod] = useState<
    "excel" | "csv" | "template"
  >("template");
  const [updateMode, setUpdateMode] = useState(true);
  const { toast } = useToast();

  const downloadTemplate = () => {
    try {
      // Prepare template data with current products
      const templateData = products.map((product) => ({
        ID: product.id, // Include ID for updates
        "Código Barras": product.barcode || "",
        "Nombre Corto": product.short_name,
        "Nombre Largo": product.long_name || "",
        Descripción: product.description || "",
        SKU: product.sku || "",
        "Código de Grupo": product.group_code || "",
        "Descripción de Grupo": product.group_description || "",
        "Código de Subgrupo": product.subgroup_code || "",
        "Descripción de Subgrupo": product.subgroup_description || "",
        "Precio Neto": product.net_price || 0,
        "Precio Sugerido": product.suggested_price || 0,
        "Precio por Kilos": product.price_in_kilos || 0,
        Stock: product.stock,
        "Peso (kg)": product.weight_kg || 0,
        "Volumen (cm³)": product.volume_cm3 || 0,
        "Código de Unidad": product.unit_code || "",
        "Unidad Base de Medida": product.base_measurement_unit || "",
        "URL de Imagen": product.product_image_url || "",
        "Visible para Vendedores":
          product.visible_to_sellers !== false ? "Sí" : "No",
        "Fecha de Creación": product.created_at,
        "Fecha de Actualización": product.updated_at,
        Estado: product.deleted_at ? "Eliminado" : "Activo",
      }));

      // Create workbook and worksheet
      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(templateData);

      // Set column widths for better readability
      const columnWidths = [
        { wch: 36 }, // ID
        { wch: 15 }, // Código Barras
        { wch: 20 }, // Nombre Corto
        { wch: 30 }, // Nombre Largo
        { wch: 40 }, // Descripción
        { wch: 15 }, // SKU
        { wch: 15 }, // Código de Grupo
        { wch: 25 }, // Descripción de Grupo
        { wch: 15 }, // Código de Subgrupo
        { wch: 25 }, // Descripción de Subgrupo
        { wch: 15 }, // Precio Neto
        { wch: 15 }, // Precio Sugerido
        { wch: 15 }, // Precio por Kilos
        { wch: 10 }, // Stock
        { wch: 15 }, // Código de Unidad
        { wch: 20 }, // Unidad Base de Medida
        { wch: 30 }, // URL de Imagen
        { wch: 20 }, // Fecha de Creación
        { wch: 20 }, // Fecha de Actualización
        { wch: 10 }, // Estado
      ];
      worksheet["!cols"] = columnWidths;

      // Add instructions sheet
      const instructions = [
        ["INSTRUCCIONES PARA ACTUALIZACIÓN DE PRODUCTOS", ""],
        ["", ""],
        ["1. IDENTIFICACIÓN DE PRODUCTOS:", ""],
        ["   - Use la columna ID para actualizar productos existentes", ""],
        ["   - Si elimina el ID, se creará un producto nuevo", ""],
        ["   - También puede usar Código Barras o SKU únicos", ""],
        ["", ""],
        ["2. EDICIÓN:", ""],
        ["   - Modifique solo las columnas que desea actualizar", ""],
        ["   - NO modifique las columnas ID, Fecha de Creación", ""],
        ["   - La columna Estado es solo informativa", ""],
        ["", ""],
        ["3. CAMPOS REQUERIDOS:", ""],
        ["   - Nombre Corto es obligatorio", ""],
        ["   - Stock debe ser un número", ""],
        ["   - Precios deben ser números decimales", ""],
        ["", ""],
        ["4. SUBIR ARCHIVO:", ""],
        ["   - Guarde el archivo como Excel (.xlsx)", ""],
        ["   - Use el botón 'Subir Excel Editado' en el diálogo", ""],
        ["   - El sistema detectará automáticamente actualizaciones", ""],
      ];

      const instructionsSheet = XLSX.utils.aoa_to_sheet(instructions);
      instructionsSheet["!cols"] = [{ wch: 50 }, { wch: 20 }];

      // Add both sheets to workbook
      XLSX.utils.book_append_sheet(
        workbook,
        instructionsSheet,
        "Instrucciones"
      );
      XLSX.utils.book_append_sheet(workbook, worksheet, "Productos");

      // Generate filename with current date
      const currentDate = new Date().toISOString().split("T")[0];
      const filename = `plantilla_productos_${currentDate}.xlsx`;

      // Write and download file
      XLSX.writeFile(workbook, filename);

      toast({
        title: "Plantilla Descargada",
        description: `Se ha descargado ${filename} con ${products.length} productos para editar.`,
      });
    } catch (error) {
      console.error("Error downloading template:", error);
      toast({
        title: "Error",
        description: "No se pudo descargar la plantilla.",
        variant: "destructive",
      });
    }
  };

  const parseExcelToProducts = (file: File): Promise<Partial<Product>[]> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = e.target?.result;
          const workbook = XLSX.read(data, { type: "binary" });

          // Look for the "Productos" sheet first, then fallback to first sheet
          const sheetName =
            workbook.SheetNames.find((name) => name === "Productos") ||
            workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

          if (jsonData.length < 2) {
            throw new Error(
              "El archivo Excel debe contener al menos un encabezado y una línea de datos"
            );
          }

          const headers = jsonData[0] as string[];
          const rows = jsonData.slice(1) as any[][];

          // Enhanced field mapping including ID for updates
          const fieldMap: Record<string, keyof Product> = {
            ID: "id",
            "Código Barras": "barcode",
            "Código barra": "barcode",
            "Nombre Corto": "short_name",
            "Nombre Largo": "long_name",
            Descripción: "description",
            "Descripción producto": "description",
            SKU: "sku",
            "Código de Grupo": "group_code",
            "Código Grupo": "group_code",
            "Descripción de Grupo": "group_description",
            "Descripción Grupo": "group_description",
            "Código de Subgrupo": "subgroup_code",
            "Código Sub-Grupo": "subgroup_code",
            "Descripción de Subgrupo": "subgroup_description",
            "Descripción Sub-Grupo": "subgroup_description",
            "Precio Neto": "net_price",
            "Precio Sugerido": "suggested_price",
            "Precio c/Impuestos": "suggested_price",
            "Precio por Kilos": "price_in_kilos",
            "Peso en kilos": "price_in_kilos",
            Stock: "stock",
            "Peso (kg)": "weight_kg",
            "Volumen (cm³)": "volume_cm3",
            "Código de Unidad": "unit_code",
            "Código unidad de medida base": "unit_code",
            "Unidad Base de Medida": "base_measurement_unit",
            "URL de Imagen": "product_image_url",
            "Visible para Vendedores": "visible_to_sellers",
          };

          const products: Partial<Product>[] = [];

          for (const row of rows) {
            if (!row || row.length === 0) continue;

            const product: Partial<Product> = {
              stock: 0, // Default value
            };

            headers.forEach((header, index) => {
              const field = fieldMap[header];
              if (field) {
                // Handle missing or empty values
                if (
                  row[index] === undefined ||
                  row[index] === null ||
                  row[index] === ""
                ) {
                  // For update mode, explicitly set missing fields to null to clear them
                  (product as any)[field] = null;
                  return;
                }

                const value = String(row[index]).trim();

                if (
                  value === "" ||
                  value === "No eliminado" ||
                  value === "Activo" ||
                  value === "Eliminado"
                ) {
                  // For update mode, explicitly set empty values to null to clear them
                  (product as any)[field] = null;
                  return;
                }

                // Handle ID field specially
                if (field === "id") {
                  (product as any)[field] = value;
                  return;
                }

                // Convert numeric values to numbers
                if (
                  field === "net_price" ||
                  field === "suggested_price" ||
                  field === "price_in_kilos" ||
                  field === "weight_kg" ||
                  field === "volume_cm3"
                ) {
                  const numValue = value
                    .replace(/[^\d.,]/g, "")
                    .replace(",", ".");
                  (product as any)[field] = numValue
                    ? parseFloat(numValue)
                    : undefined;
                } else if (field === "stock") {
                  // Handle stock values more robustly
                  const cleanValue = value
                    .replace(/[^\d.,]/g, "")
                    .replace(",", ".");
                  const stockValue = parseFloat(cleanValue);
                  const finalStockValue = isNaN(stockValue)
                    ? 0
                    : Math.max(0, Math.round(stockValue));
                  (product as any)[field] = finalStockValue;
                } else if (field === "visible_to_sellers") {
                  // Handle boolean values represented as "Sí"/"No"
                  (product as any)[field] =
                    value.toLowerCase() === "sí" ||
                    value.toLowerCase() === "si" ||
                    value.toLowerCase() === "yes" ||
                    value.toLowerCase() === "true";
                } else {
                  // String values - capitalize product names
                  let stringValue = value;
                  if (
                    field === "description" ||
                    field === "short_name" ||
                    field === "long_name" ||
                    field === "group_description" ||
                    field === "subgroup_description" ||
                    field === "group_code" ||
                    field === "subgroup_code"
                  ) {
                    stringValue = stringValue.toUpperCase();
                  }
                  (product as any)[field] = stringValue;
                }
              }
            });

            // Ensure we have either an ID (for updates) or required fields for new products
            if (
              product.id ||
              product.barcode ||
              product.description ||
              product.short_name
            ) {
              // If no short_name but we have description, use description as short_name
              if (!product.short_name && product.description) {
                product.short_name = product.description;
              }
              products.push(product);
            }
          }

          resolve(products);
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = () => reject(new Error("Error reading the Excel file"));
      reader.readAsBinaryString(file);
    });
  };

  const parseCSVToProducts = (csvText: string): Partial<Product>[] => {
    try {
      // Split by lines and filter out empty lines
      const lines = csvText
        .split("\n")
        .filter((line) => line.trim().length > 0);

      if (lines.length < 2) {
        throw new Error(
          "El archivo CSV debe contener al menos un encabezado y una línea de datos"
        );
      }

      // Get headers
      const headers = lines[0].split(/\t|,|;/).map((header) => header.trim());

      // Enhanced field mapping for both formats
      const fieldMap: Record<string, keyof Product> = {
        ID: "id",
        "Código Barras": "barcode",
        "Código barra": "barcode",
        "Nombre Corto": "short_name",
        "Nombre Largo": "long_name",
        Descripción: "description",
        "Descripción producto": "description",
        SKU: "sku",
        "Código de Grupo": "group_code",
        "Código Grupo": "group_code",
        "Descripción de Grupo": "group_description",
        "Descripción Grupo": "group_description",
        "Código de Subgrupo": "subgroup_code",
        "Código Sub-Grupo": "subgroup_code",
        "Descripción de Subgrupo": "subgroup_description",
        "Descripción Sub-Grupo": "subgroup_description",
        "Precio Neto": "net_price",
        "Precio Sugerido": "suggested_price",
        "Precio c/Impuestos": "suggested_price",
        "Precio por Kilos": "price_in_kilos",
        "Peso en kilos": "price_in_kilos",
        Stock: "stock",
        "Peso (kg)": "weight_kg",
        "Volumen (cm³)": "volume_cm3",
        "Código de Unidad": "unit_code",
        "Código unidad de medida base": "unit_code",
        "Unidad Base de Medida": "base_measurement_unit",
        "URL de Imagen": "product_image_url",
        "Visible para Vendedores": "visible_to_sellers",
      };

      // Create products from data lines
      const products: Partial<Product>[] = [];

      for (let i = 1; i < lines.length; i++) {
        const values = lines[i].split(/\t|,|;/).map((value) => value.trim());

        if (values.length !== headers.length) {
          console.warn(
            `La línea ${i + 1} no tiene el número correcto de columnas y será omitida`
          );
          continue;
        }

        const product: Partial<Product> = {
          stock: 0, // Default value
        };

        // Assign values to product fields based on header mapping
        headers.forEach((header, index) => {
          const field = fieldMap[header];
          if (field) {
            // Handle missing or empty values
            if (
              !values[index] ||
              values[index] === "" ||
              values[index] === "No eliminado"
            ) {
              // For update mode, explicitly set missing fields to null to clear them
              (product as any)[field] = null;
              return;
            }
            // Handle ID field specially
            if (field === "id") {
              (product as any)[field] = values[index];
              return;
            }

            // Convert numeric values to numbers
            if (
              field === "net_price" ||
              field === "suggested_price" ||
              field === "price_in_kilos" ||
              field === "weight_kg" ||
              field === "volume_cm3"
            ) {
              // Remove any non-numeric characters and convert to number
              const numValue = values[index]
                .replace(/[^\d.,]/g, "")
                .replace(",", ".");
              (product as any)[field] = numValue
                ? parseFloat(numValue)
                : undefined;
            } else if (field === "stock") {
              // Handle stock values more robustly
              const cleanValue = values[index]
                .replace(/[^\d.,]/g, "")
                .replace(",", ".");
              const stockValue = parseFloat(cleanValue);
              (product as any)[field] = isNaN(stockValue)
                ? 0
                : Math.max(0, Math.round(stockValue));
            } else if (field === "visible_to_sellers") {
              // Handle boolean values represented as "Sí"/"No"
              (product as any)[field] =
                values[index].toLowerCase() === "sí" ||
                values[index].toLowerCase() === "si" ||
                values[index].toLowerCase() === "yes" ||
                values[index].toLowerCase() === "true";
            } else {
              // String values - capitalize product names
              let stringValue = values[index] || undefined;
              if (
                field === "description" ||
                field === "short_name" ||
                field === "long_name" ||
                field === "group_description" ||
                field === "subgroup_description" ||
                field === "group_code" ||
                field === "subgroup_code"
              ) {
                stringValue = stringValue
                  ? stringValue.toUpperCase()
                  : undefined;
              }
              (product as any)[field] = stringValue;
            }
          }
        });

        // Ensure we have either an ID (for updates) or required fields for new products
        if (
          product.id ||
          product.barcode ||
          product.description ||
          product.short_name
        ) {
          // If no short_name but we have description, use description as short_name
          if (!product.short_name && product.description) {
            product.short_name = product.description;
          }
          products.push(product);
        }
      }

      return products;
    } catch (error) {
      console.error("Error parsing CSV:", error);
      throw new Error(
        "Error al procesar el archivo CSV. Verifique el formato."
      );
    }
  };

  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setIsProcessing(true);
      const products = await parseExcelToProducts(file);

      if (products.length === 0) {
        toast({
          title: "Error de importación",
          description: "No se pudieron extraer productos del archivo Excel.",
          variant: "destructive",
        });
        return;
      }

      // Enhanced detection: update mode if has IDs OR if barcodes match existing products
      const hasIds = products.some((p) => p.id);

      // If no IDs, check if barcodes match existing products
      let hasBarcodeMatches = false;
      if (!hasIds) {
        const existingBarcodes = new Set(
          products.map((p) => p.barcode).filter(Boolean)
        );
        const currentBarcodes = new Set(
          products.map((p) => p.barcode).filter(Boolean)
        );

        // Check if any imported barcodes match existing products
        const { data: existingProducts } = await supabase
          .from("products")
          .select("barcode")
          .in("barcode", Array.from(currentBarcodes))
          .is("deleted_at", null);

        hasBarcodeMatches = (existingProducts?.length || 0) > 0;
      }

      const detectedUpdateMode = hasIds || hasBarcodeMatches;

      await onImport(products, detectedUpdateMode);

      // Clear the file input
      if (event.target) {
        event.target.value = "";
      }
    } catch (error) {
      console.error("Excel import error:", error);
      toast({
        title: "Error de importación",
        description:
          error instanceof Error
            ? error.message
            : "Error al importar desde Excel.",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCSVImport = async () => {
    try {
      setIsProcessing(true);

      // Parse the CSV data
      const products = parseCSVToProducts(csvData);

      if (products.length === 0) {
        toast({
          title: "Error de importación",
          description:
            "No se pudieron extraer productos del texto proporcionado.",
          variant: "destructive",
        });
        return;
      }

      // Enhanced detection: update mode if has IDs OR if barcodes match existing products
      const hasIds = products.some((p) => p.id);

      // If no IDs, check if barcodes match existing products
      let hasBarcodeMatches = false;
      if (!hasIds) {
        const currentBarcodes = new Set(
          products.map((p) => p.barcode).filter(Boolean)
        );

        // Check if any imported barcodes match existing products
        const { data: existingProducts } = await supabase
          .from("products")
          .select("barcode")
          .in("barcode", Array.from(currentBarcodes))
          .is("deleted_at", null);

        hasBarcodeMatches = (existingProducts?.length || 0) > 0;
      }

      const detectedUpdateMode = hasIds || hasBarcodeMatches;

      // Log detection for debugging
      console.log(`🔍 CSV Update mode detection:`, {
        hasIds,
        hasBarcodeMatches,
        detectedUpdateMode,
        productsCount: products.length,
      });

      // Call the import function
      await onImport(products, detectedUpdateMode);

      // Clear the textarea
      setCsvData("");
    } catch (error) {
      console.error("Import error:", error);
      toast({
        title: "Error de importación",
        description:
          error instanceof Error
            ? error.message
            : "Error al importar productos.",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[700px]">
        <DialogHeader>
          <DialogTitle>Gestión de Productos</DialogTitle>
          <DialogDescription>
            Descargue la plantilla, edite los productos, y suba el archivo
            actualizado para aplicar cambios masivos.
          </DialogDescription>
        </DialogHeader>

        <div className="my-4 space-y-4">
          {/* Method Selection */}
          <div className="flex gap-2">
            <Button
              variant={importMethod === "template" ? "default" : "outline"}
              onClick={() => setImportMethod("template")}
              disabled={isProcessing}
              className="flex-1"
            >
              <FileSpreadsheet className="mr-2 h-4 w-4" />
              Plantilla Excel
            </Button>
            <Button
              variant={importMethod === "excel" ? "default" : "outline"}
              onClick={() => setImportMethod("excel")}
              disabled={isProcessing}
              className="flex-1"
            >
              <Upload className="mr-2 h-4 w-4" />
              Subir Excel
            </Button>
            <Button
              variant={importMethod === "csv" ? "default" : "outline"}
              onClick={() => setImportMethod("csv")}
              disabled={isProcessing}
              className="flex-1"
            >
              Texto CSV
            </Button>
          </div>

          {/* Template Download */}
          {importMethod === "template" && (
            <div className="space-y-4">
              <Alert>
                <Info className="h-4 w-4" />
                <AlertDescription>
                  <strong>Flujo de trabajo recomendado:</strong>
                  <ol className="mt-2 list-inside list-decimal space-y-1 text-sm">
                    <li>Descargue la plantilla con los productos actuales</li>
                    <li>Edite los valores que desea cambiar en Excel</li>
                    <li>Guarde el archivo y súbalo usando "Subir Excel"</li>
                    <li>
                      El sistema actualizará automáticamente los productos
                    </li>
                  </ol>
                </AlertDescription>
              </Alert>

              <div className="flex justify-center">
                <Button
                  onClick={downloadTemplate}
                  disabled={products.length === 0}
                  className="w-full max-w-sm"
                >
                  <Download className="mr-2 h-4 w-4" />
                  Descargar Plantilla ({products.length} productos)
                </Button>
              </div>

              <p className="text-center text-sm text-muted-foreground">
                La plantilla incluye todos los productos actuales con sus IDs
                para permitir actualizaciones. También incluye una hoja de
                instrucciones detalladas.
              </p>
            </div>
          )}

          {/* Excel Upload */}
          {importMethod === "excel" && (
            <div className="space-y-2">
              <Input
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileUpload}
                disabled={isProcessing}
                className="file:mr-4 file:rounded-full file:border-0 file:bg-primary file:px-4 file:py-2 file:text-sm file:font-semibold file:text-primary-foreground hover:file:bg-primary/90"
              />
              <Alert>
                <Info className="h-4 w-4" />
                <AlertDescription>
                  <strong>Actualizaciones automáticas:</strong> El sistema puede
                  actualizar productos existentes de dos formas:
                  <ul className="mt-2 list-inside list-disc space-y-1 text-sm">
                    <li>
                      <strong>Por ID:</strong> Si el archivo contiene la columna
                      ID con valores UUID
                    </li>
                    <li>
                      <strong>Por Código de Barras:</strong> Si los códigos de
                      barras coinciden con productos existentes
                    </li>
                    <li>
                      <strong>Crear nuevos:</strong> Si no hay coincidencias, se
                      crearán productos nuevos
                    </li>
                  </ul>
                  Use la plantilla descargada para garantizar actualizaciones
                  por ID.
                </AlertDescription>
              </Alert>
            </div>
          )}

          {/* CSV Text Input */}
          {importMethod === "csv" && (
            <div className="space-y-2">
              <Textarea
                placeholder="Pegue los datos CSV aquí..."
                value={csvData}
                onChange={(e) => setCsvData(e.target.value)}
                className="h-[300px] font-mono text-sm"
                disabled={isProcessing}
              />
              <div className="text-sm text-muted-foreground">
                <p>
                  Formato esperado de encabezados (separados por coma, punto y
                  coma o tab):
                </p>
                <p className="mt-1 font-mono text-xs">
                  Código Barras, Nombre Corto, Descripción, Código de Grupo,
                  etc.
                </p>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isProcessing}
          >
            Cancelar
          </Button>
          {importMethod === "csv" && (
            <Button
              onClick={handleCSVImport}
              disabled={!csvData.trim() || isProcessing}
            >
              {isProcessing ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Procesando...
                </>
              ) : (
                "Importar CSV"
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
